import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { namespaceOf } from '@/i18n';
import { Skeleton } from '@/ui';
import { buildNavigation, navTitleKey, sectionForPath, sectionHref, sectionNamespaces, sections as registeredSections, UNTITLED_NAV_GROUPS, type SectionDefinition } from '../registry';
import type { ClinicRole, StaffRole } from '../session/roles';
import s from '../layouts/Layout.module.css';

export interface SideNavProps {
  area: 'clinic' | 'backoffice';
  roles: ReadonlyArray<ClinicRole | StaffRole>;
  /** Для тестов и витрины — другой список разделов. */
  sections?: ReadonlyArray<SectionDefinition>;
  onNavigate?: () => void;
}

const counterCache = new Map<string, LazyExoticComponent<ComponentType>>();

function lazyCounter(section: SectionDefinition): LazyExoticComponent<ComponentType> | null {
  if (!section.navCounter) return null;
  let component = counterCache.get(section.id);
  if (!component) {
    component = lazy(section.navCounter);
    counterCache.set(section.id, component);
  }
  return component;
}

/**
 * Боковое меню по ролям (DESIGN.md 1.5): белое, пункты без иконок, группы подписаны мелким серым,
 * активный пункт — фон bg.subtle и вес 600. Раздел группы вкладок — один пункт, активный на любой её вкладке.
 */
export function SideNav({ area, roles, sections = registeredSections, onNavigate }: SideNavProps) {
  const groups = buildNavigation(sections, area, roles);
  const items = groups.flatMap((group) => group.items);
  const namespaces = ['shell', ...new Set(items.flatMap((section) => [...sectionNamespaces(section), namespaceOf(navTitleKey(section))]))];
  const { t, ready } = useTranslation(namespaces);
  const { pathname } = useLocation();
  const current = sectionForPath(pathname, sections);
  return (
    <nav aria-label={t('shell:nav.label')} className={s.navInner} aria-busy={!ready || undefined}>
      {groups.length === 0 ? <p className={s.navEmpty}>{t('shell:nav.empty')}</p> : null}
      {groups.map(({ group, items: groupItems }) => (
        <div key={group} className={s.navGroup}>
          <h2 className={UNTITLED_NAV_GROUPS.has(group) ? 'visually-hidden' : s.navGroupTitle} id={`nav-group-${group}`}>
            {t(`shell:navGroups.${group}`)}
          </h2>
          <ul className={s.navList} aria-labelledby={`nav-group-${group}`}>
            {groupItems.map((section) => {
              const href = sectionHref(section);
              const active = current !== null && (current.id === section.id || (section.tabGroup !== undefined && current.tabGroup === section.tabGroup));
              const Counter = lazyCounter(section);
              return (
                <li key={section.id}>
                  <Link to={href} className={s.navLink} onClick={onNavigate} aria-current={active ? 'page' : undefined}>
                    {ready ? <span>{t(navTitleKey(section))}</span> : <Skeleton width={120} height={12} />}
                    {Counter ? (
                      <Suspense fallback={null}>
                        <Counter />
                      </Suspense>
                    ) : null}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

/** Число у пункта меню: цвет по смыслу (danger — красный флаг, accent — новое для вас), ноль не показывается. */
export function NavCount({ value, tone = 'neutral', label }: { value: number; tone?: 'neutral' | 'accent' | 'danger'; label: string }) {
  if (value <= 0) return null;
  const toneClass = tone === 'danger' ? s.navCountDanger : tone === 'accent' ? s.navCountAccent : '';
  return (
    <span className={`${s.navCount} ${toneClass}`}>
      <span aria-hidden="true">{value > 9 ? '9+' : value}</span>
      <span className="visually-hidden">{label}</span>
    </span>
  );
}
